import { useState, useEffect, useCallback, useRef } from 'react'
import { api } from '../context/AuthContext'

// ── Status mapping — refund DB values → display ───────────────────────────────
const STATUS_DISPLAY = {
  pending:    { bg: '#fef2f2', color: '#b91c1c', dot: '#ef4444', label: 'Open'         },
  approved:   { bg: '#fffbeb', color: '#b45309', dot: '#f59e0b', label: 'Under Review' },
  processing: { bg: '#fffbeb', color: '#b45309', dot: '#f59e0b', label: 'Under Review' },
  refunded:   { bg: '#f0fdf4', color: '#15803d', dot: '#16a34a', label: 'Resolved'     },
  rejected:   { bg: '#f3f4f6', color: '#374151', dot: '#9ca3af', label: 'Dismissed'    },
}
const statusDisplay = (s) => STATUS_DISPLAY[s] ?? STATUS_DISPLAY.pending

// Filter tabs — use real DB status values as the query param
const FILTER_TABS = [
  { value: '',           label: 'All'          },
  { value: 'pending',    label: 'Open'         },
  { value: 'approved',   label: 'Under Review' },
  { value: 'refunded',   label: 'Resolved'     },
  { value: 'rejected',   label: 'Dismissed'    },
]

const REASONS = [
  { value: 'item_not_received',     label: 'Item Not Received'   },
  { value: 'item_not_as_described', label: 'Not As Described'    },
  { value: 'wrong_item',            label: 'Wrong Item Sent'     },
  { value: 'damaged_item',          label: 'Damaged Item'        },
  { value: 'not_shipped',           label: 'Not Shipped'         },
  { value: 'seller_cancel',         label: 'Seller Cancelled'    },
  { value: 'other',                 label: 'Other'               },
]
const reasonLabel = (v) => REASONS.find(r => r.value === v)?.label || v

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtTs = (d) => d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'
const rwf   = (n) => n != null ? `RWF ${Number(n).toLocaleString()}` : '—'

// ── StatusBadge ───────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const s = statusDisplay(status)
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: s.bg, color: s.color, fontSize: 10, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", padding: '3px 8px', borderRadius: 4, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: s.dot }} />{s.label}
    </span>
  )
}

// ── StatCard ──────────────────────────────────────────────────────────────────
function StatCard({ label, value, accent }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, padding: '18px 20px', flex: 1 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono',monospace", marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.6px', color: accent || '#111317' }}>{value}</div>
    </div>
  )
}

function Row({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '9px 0', borderBottom: '1px solid #f3f3f0' }}>
      <span style={{ fontSize: 12, color: '#9a9a94', fontWeight: 600, flexShrink: 0, marginRight: 12 }}>{label}</span>
      <span style={{ fontSize: 12, fontWeight: 700, color: '#111317', textAlign: 'right', maxWidth: 300, wordBreak: 'break-word' }}>{value}</span>
    </div>
  )
}

// ── Refund Drawer ─────────────────────────────────────────────────────────────
function RefundDrawer({ refund, onClose, onUpdated }) {
  const [detail,   setDetail]   = useState(null)
  const [loading,  setLoading]  = useState(false)
  const [tab,      setTab]      = useState('details')
  const [note,     setNote]     = useState('')
  const [refRef,   setRefRef]   = useState('')
  const [saving,   setSaving]   = useState(false)
  const [noteErr,  setNoteErr]  = useState('')

  useEffect(() => {
    if (!refund) return
    setTab('details')
    setDetail(null)
    setNote('')
    setRefRef('')
    setNoteErr('')
    setLoading(true)
    api.get(`/refunds/admin/${refund.refund_id}`)
      .then(res => setDetail(res.data.refund))
      .catch(() => setDetail(refund))
      .finally(() => setLoading(false))
  }, [refund?.refund_id])

  if (!refund) return null
  const d = detail || refund

  // ── Actions map ──────────────────────────────────────────────
  // approve  → sets status to 'approved' (Under Review + IremboPay instructions)
  // reject   → sets status to 'rejected', admin_note REQUIRED
  // refunded → marks as fully refunded, optional refund_ref
  const canApprove  = d.status === 'pending'
  const canRefund   = d.status === 'approved' || d.status === 'processing'
  const canReject   = d.status === 'pending'

  const act = async (action) => {
    setNoteErr('')
    if (action === 'reject' && !note.trim()) {
      setNoteErr('A reason is required — the buyer will see this message.')
      return
    }
    setSaving(true)
    try {
      let res
      if (action === 'approve') {
        res = await api.post(`/refunds/admin/${d.refund_id}/approve`, { admin_note: note.trim() || null })
        const updated = { ...d, status: 'approved', admin_note: note.trim() || d.admin_note }
        setDetail(updated); onUpdated(updated)
      } else if (action === 'reject') {
        res = await api.post(`/refunds/admin/${d.refund_id}/reject`, { admin_note: note.trim() })
        const updated = { ...d, status: 'rejected', admin_note: note.trim() }
        setDetail(updated); onUpdated(updated)
      } else if (action === 'mark-refunded') {
        res = await api.post(`/refunds/admin/${d.refund_id}/mark-refunded`, {
          refund_ref:  refRef.trim() || null,
          admin_note:  note.trim()   || null,
        })
        const updated = { ...d, status: 'refunded', refund_ref: refRef.trim() || null, admin_note: note.trim() || d.admin_note }
        setDetail(updated); onUpdated(updated)
      }
      setNote('')
      setRefRef('')
    } catch (err) {
      alert(err.response?.data?.error || 'Action failed')
    } finally { setSaving(false) }
  }

  const evidence = (() => {
    try { return Array.isArray(d.evidence) ? d.evidence : JSON.parse(d.evidence || '[]') } catch { return [] }
  })()

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.2)', zIndex: 200 }} />
      <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 500, background: '#fff', zIndex: 201, display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 40px rgba(0,0,0,0.12)', animation: 'slideIn 0.22s ease' }}>
        <style>{`@keyframes slideIn{from{transform:translateX(40px);opacity:0}to{transform:translateX(0);opacity:1}}`}</style>

        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #e9e9e4', display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 800 }}>Refund #{d.refund_id}</div>
            <div style={{ fontSize: 12, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", marginTop: 2 }}>
              {reasonLabel(d.reason)} · Order #{d.order_id} · {fmtTs(d.created_at)}
            </div>
          </div>
          <StatusBadge status={d.status} />
          <div onClick={onClose} style={{ cursor: 'pointer', fontSize: 20, color: '#9a9a94', marginLeft: 4 }}>×</div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid #e9e9e4', padding: '0 24px' }}>
          {[['details','Details'],['evidence','Evidence']].map(([id, lbl]) => (
            <button key={id} onClick={() => setTab(id)}
              style={{ padding: '12px 16px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: "'Plus Jakarta Sans',sans-serif", color: tab === id ? '#111317' : '#9a9a94', borderBottom: `2px solid ${tab === id ? '#e8440a' : 'transparent'}`, marginBottom: -1 }}>
              {lbl}{id === 'evidence' && evidence.length > 0 ? ` (${evidence.length})` : ''}
            </button>
          ))}
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
          {loading && <div style={{ textAlign: 'center', color: '#9a9a94', fontSize: 13, padding: '32px 0' }}>Loading…</div>}

          {!loading && tab === 'details' && (
            <>
              {/* Buyer card */}
              <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 10, padding: '12px 14px', marginBottom: 16 }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase', marginBottom: 6 }}>Buyer</div>
                <div style={{ fontSize: 13, fontWeight: 700 }}>{d.username || '—'}</div>
                <div style={{ fontSize: 11, color: '#6b7280', fontFamily: "'JetBrains Mono',monospace" }}>{d.phone || d.email || '—'}</div>
              </div>

              {[
                ['Refund ID',     `#${d.refund_id}`],
                ['Order ID',      `#${d.order_id}`],
                ['Reason',        reasonLabel(d.reason)],
                ['Amount',        rwf(d.amount)],
                ['Order Status',  d.order_status || '—'],
                ['Payment Method',d.payment_method || '—'],
                ['Status',        <StatusBadge key="s" status={d.status} />],
                ['Submitted',     fmtTs(d.created_at)],
                ['Last Updated',  fmtTs(d.updated_at)],
              ].map(([k, v]) => <Row key={k} label={k} value={v} />)}

              {d.details && (
                <div style={{ marginTop: 16, background: '#f9f9f7', borderRadius: 9, padding: '12px 14px' }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase', marginBottom: 6 }}>Buyer's Details</div>
                  <div style={{ fontSize: 13, color: '#111317', lineHeight: 1.6 }}>{d.details}</div>
                </div>
              )}

              {d.admin_note && (
                <div style={{ marginTop: 12, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 9, padding: '12px 14px' }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#b45309', fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase', marginBottom: 6 }}>Admin Note</div>
                  <div style={{ fontSize: 13, color: '#92400e', lineHeight: 1.6 }}>{d.admin_note}</div>
                </div>
              )}

              {d.refund_ref && (
                <div style={{ marginTop: 12, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 9, padding: '12px 14px' }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#15803d', fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase', marginBottom: 4 }}>Refund Reference</div>
                  <div style={{ fontSize: 13, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", color: '#15803d' }}>{d.refund_ref}</div>
                </div>
              )}

              {/* IremboPay instructions when approved */}
              {d.status === 'approved' && (
                <div style={{ marginTop: 12, background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 9, padding: '12px 14px' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#0369a1', marginBottom: 4 }}>📋 Next Step — Process in IremboPay</div>
                  <div style={{ fontSize: 12, color: '#0369a1', lineHeight: 1.6 }}>
                    Go to <b>dashboard.irembopay.com</b> → Transactions → search by <b>{d.payment_ref || 'payment ref'}</b> → click <b>Reverse</b>. Then come back and click "Mark Refunded" below.
                  </div>
                </div>
              )}
            </>
          )}

          {!loading && tab === 'evidence' && (
            evidence.length === 0
              ? <div style={{ textAlign: 'center', color: '#9a9a94', fontSize: 13, padding: '40px 0' }}>No evidence submitted.</div>
              : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {evidence.map((e, i) => (
                    <div key={i} style={{ border: '1px solid #e9e9e4', borderRadius: 10, overflow: 'hidden' }}>
                      {e.type === 'photo' || e.mimetype?.startsWith('image/') ? (
                        <img src={e.url} alt={`Evidence ${i + 1}`} style={{ width: '100%', display: 'block', maxHeight: 300, objectFit: 'contain', background: '#f3f3f0' }}
                          onError={e => e.target.style.display='none'} />
                      ) : (
                        <div style={{ padding: '14px', display: 'flex', alignItems: 'center', gap: 10, background: '#f9f9f7' }}>
                          <span style={{ fontSize: 24 }}>🎥</span>
                          <a href={e.url} target="_blank" rel="noreferrer" style={{ fontSize: 12, fontWeight: 700, color: '#e8440a' }}>View Video Evidence</a>
                        </div>
                      )}
                      <div style={{ padding: '8px 12px', fontSize: 10, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>
                        {e.type?.toUpperCase() || 'FILE'} · {e.mimetype || '—'}
                      </div>
                    </div>
                  ))}
                </div>
              )
          )}
        </div>

        {/* Action footer */}
        {(canApprove || canRefund || canReject) && (
          <div style={{ padding: '16px 24px', borderTop: '1px solid #e9e9e4', background: '#fafaf8' }}>
            {canRefund && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase', marginBottom: 5 }}>IremboPay Refund Ref (optional)</div>
                <input value={refRef} onChange={e => setRefRef(e.target.value)} placeholder="e.g. REF-123456789"
                  style={{ width: '100%', height: 36, borderRadius: 7, border: '1px solid #e9e9e4', padding: '0 10px', fontSize: 12, outline: 'none', fontFamily: "'Plus Jakarta Sans',sans-serif", boxSizing: 'border-box' }} />
              </div>
            )}
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase', marginBottom: 5 }}>
                {canReject ? 'Reason for Dismissal *' : 'Admin Note (optional)'}
              </div>
              <textarea value={note} onChange={e => { setNote(e.target.value); setNoteErr('') }} rows={2}
                placeholder={canReject ? 'Explain why this refund is being dismissed — buyer will see this…' : 'Optional note for the record…'}
                style={{ width: '100%', borderRadius: 8, border: `1px solid ${noteErr ? '#fca5a5' : '#e9e9e4'}`, padding: '8px 10px', fontSize: 12, outline: 'none', resize: 'none', fontFamily: "'Plus Jakarta Sans',sans-serif", boxSizing: 'border-box' }} />
              {noteErr && <div style={{ fontSize: 11, color: '#b91c1c', marginTop: 3 }}>{noteErr}</div>}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {canApprove && (
                <button onClick={() => act('approve')} disabled={saving}
                  style={{ flex: 1, height: 38, borderRadius: 7, border: 'none', background: '#fffbeb', color: '#b45309', fontSize: 12, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", border: '1px solid #fde68a' }}>
                  Review →
                </button>
              )}
              {canRefund && (
                <button onClick={() => act('mark-refunded')} disabled={saving}
                  style={{ flex: 1, height: 38, borderRadius: 7, border: 'none', background: saving ? '#f0c4b4' : '#e8440a', color: '#fff', fontSize: 12, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                  {saving ? 'Saving…' : '✓ Mark Refunded'}
                </button>
              )}
              {canReject && (
                <button onClick={() => act('reject')} disabled={saving}
                  style={{ flex: 1, height: 38, borderRadius: 7, border: '1px solid #e9e9e4', background: '#fff', color: '#374151', fontSize: 12, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                  Dismiss
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  )
}

// ── PagBtn ────────────────────────────────────────────────────────────────────
function PagBtn({ label, onClick, disabled, active }) {
  return (
    <button onClick={onClick} disabled={disabled}
      style={{ minWidth: 34, height: 34, borderRadius: 7, border: '1px solid #e9e9e4', background: active ? '#111317' : '#fff', color: active ? '#fff' : disabled ? '#9a9a94' : '#111317', fontSize: 12, fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer', padding: '0 10px', fontFamily: "'Plus Jakarta Sans',sans-serif", opacity: disabled ? 0.4 : 1 }}>
      {label}
    </button>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function DisputeResolution() {
  const [refunds,     setRefunds]     = useState([])
  const [loading,     setLoading]     = useState(false)
  const [stats,       setStats]       = useState({ pending: 0, approved: 0, refunded: 0, rejected: 0, processing: 0 })
  const [statusTab,   setStatusTab]   = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [search,      setSearch]      = useState('')
  const [page,        setPage]        = useState(1)
  const [totalPages,  setTotalPages]  = useState(1)
  const [total,       setTotal]       = useState(0)
  const [selected,    setSelected]    = useState(null)
  const fetchRef = useRef(0)
  const PER_PAGE = 15

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 400)
    return () => clearTimeout(t)
  }, [searchInput])

  useEffect(() => { setPage(1) }, [search, statusTab])

  const fetchRefunds = useCallback(async () => {
    const id = ++fetchRef.current
    setLoading(true)
    try {
      const params = new URLSearchParams({
        page, limit: PER_PAGE,
        ...(search    && { search }),
        ...(statusTab && { status: statusTab }),
      })
      const res = await api.get(`/refunds/admin?${params}`)
      if (id !== fetchRef.current) return
      setRefunds(res.data.refunds    || [])
      setTotalPages(res.data.totalPages || 1)
      setTotal(res.data.total         || 0)
      setStats(res.data.stats         || {})
    } catch {
      if (id !== fetchRef.current) return
      setRefunds([])
    } finally {
      if (id === fetchRef.current) setLoading(false)
    }
  }, [page, search, statusTab])

  useEffect(() => { fetchRefunds() }, [fetchRefunds])

  const handleUpdated = (updated) => {
    setRefunds(prev => prev.map(r => r.refund_id === updated.refund_id ? { ...r, ...updated } : r))
  }

  const openCount     = Number(stats.pending    || 0)
  const reviewCount   = Number(stats.approved   || 0) + Number(stats.processing || 0)
  const resolvedCount = Number(stats.refunded   || 0)
  const dismissCount  = Number(stats.rejected   || 0)
  const totalCount    = openCount + reviewCount + resolvedCount + dismissCount

  const selectStyle = { border: '1px solid #e9e9e4', borderRadius: 7, padding: '8px 12px', fontSize: 12, fontWeight: 600, color: '#111317', background: '#fff', cursor: 'pointer', outline: 'none', fontFamily: "'Plus Jakarta Sans',sans-serif" }

  return (
    <div style={{ padding: '28px 32px 60px', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.4px' }}>Dispute Resolution</div>
        <div style={{ fontSize: 13, color: '#9a9a94', marginTop: 4 }}>Review buyer refund requests, approve or dismiss, then process payouts via IremboPay.</div>
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: 14, marginBottom: 24 }}>
        <StatCard label="Total"        value={totalCount}    />
        <StatCard label="Open"         value={openCount}     accent="#b91c1c" />
        <StatCard label="Under Review" value={reviewCount}   accent="#b45309" />
        <StatCard label="Resolved"     value={resolvedCount} accent="#15803d" />
        <StatCard label="Dismissed"    value={dismissCount}  accent="#9ca3af" />
      </div>

      {/* Open alert */}
      {openCount > 0 && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderLeft: '3px solid #ef4444', borderRadius: 8, padding: '10px 16px', marginBottom: 16, fontSize: 13, display: 'flex', alignItems: 'center', gap: 10 }}>
          <span>🚨</span>
          <span><b>{openCount} open refund request{openCount > 1 ? 's' : ''}</b> awaiting review</span>
          <span onClick={() => setStatusTab('pending')} style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 700, color: '#b91c1c', cursor: 'pointer', fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase' }}>View Open →</span>
        </div>
      )}

      {/* Status tabs */}
      <div style={{ display: 'flex', background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, overflow: 'hidden', marginBottom: 12 }}>
        {FILTER_TABS.map(tab => {
          const cnt = tab.value === '' ? totalCount : tab.value === 'pending' ? openCount : tab.value === 'approved' ? reviewCount : tab.value === 'refunded' ? resolvedCount : dismissCount
          return (
            <button key={tab.value} onClick={() => setStatusTab(tab.value)}
              style={{ flex: 1, padding: '10px 0', border: 'none', background: statusTab === tab.value ? '#111317' : 'transparent', color: statusTab === tab.value ? '#fff' : '#374151', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", transition: 'all 0.12s' }}>
              {tab.label}
              {cnt > 0 && (
                <span style={{ marginLeft: 6, background: statusTab === tab.value ? 'rgba(255,255,255,0.2)' : '#e9e9e4', color: statusTab === tab.value ? '#fff' : '#374151', fontSize: 10, fontWeight: 800, padding: '1px 5px', borderRadius: 3, fontFamily: "'JetBrains Mono',monospace" }}>
                  {cnt}
                </span>
              )}
            </button>
          )
        })}
      </div>

      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, padding: '12px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, background: '#f6f6f3', border: '1px solid #e9e9e4', borderRadius: 7, padding: '7px 12px' }}>
          <span style={{ fontSize: 14, color: '#9a9a94' }}>🔍</span>
          <input value={searchInput} onChange={e => setSearchInput(e.target.value)} placeholder="Search by buyer name, phone or order ID…"
            style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 13, color: '#111317', flex: 1, fontFamily: "'Plus Jakarta Sans',sans-serif" }} />
          {searchInput && <span onClick={() => { setSearchInput(''); setSearch('') }} style={{ cursor: 'pointer', color: '#9a9a94', fontSize: 16 }}>×</span>}
        </div>
        <button onClick={fetchRefunds} style={{ height: 36, width: 36, borderRadius: 7, border: '1px solid #e9e9e4', background: '#fff', cursor: 'pointer', fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>↻</button>
        <div style={{ fontSize: 12, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", whiteSpace: 'nowrap' }}>{total} total</div>
      </div>

      {/* Table */}
      <div style={{ background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '0.5fr 2fr 1.4fr 1.4fr 1fr 1fr 0.7fr', padding: '10px 20px', borderBottom: '1px solid #e9e9e4', background: '#f9f9f7' }}>
          {['#','Buyer','Order','Reason','Amount','Status',''].map(h => (
            <div key={h} style={{ fontSize: 10, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: "'JetBrains Mono',monospace" }}>{h}</div>
          ))}
        </div>

        {loading ? (
          <div style={{ padding: '48px 0', textAlign: 'center', color: '#9a9a94', fontSize: 13 }}>Loading refunds…</div>
        ) : refunds.length === 0 ? (
          <div style={{ padding: '48px 0', textAlign: 'center', color: '#9a9a94', fontSize: 13 }}>No refund requests found.</div>
        ) : refunds.map((r, i) => (
          <div key={r.refund_id}
            onClick={() => setSelected(r)}
            style={{ display: 'grid', gridTemplateColumns: '0.5fr 2fr 1.4fr 1.4fr 1fr 1fr 0.7fr', padding: '13px 20px', borderBottom: i < refunds.length - 1 ? '1px solid #f3f3f0' : 'none', cursor: 'pointer', alignItems: 'center', transition: 'background 0.12s' }}
            onMouseEnter={e => e.currentTarget.style.background = '#fafaf8'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>

            <div style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>#{r.refund_id}</div>

            <div>
              <div style={{ fontSize: 13, fontWeight: 700 }}>{r.username || '—'}</div>
              <div style={{ fontSize: 11, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>{r.phone || '—'}</div>
            </div>

            <div style={{ fontSize: 12, fontFamily: "'JetBrains Mono',monospace", fontWeight: 600 }}>#{r.order_id}</div>

            <div style={{ fontSize: 12, color: '#374151' }}>{r.reason_label || reasonLabel(r.reason)}</div>

            <div style={{ fontSize: 12, fontFamily: "'JetBrains Mono',monospace", fontWeight: 700 }}>{rwf(r.amount)}</div>

            <StatusBadge status={r.status} />

            <div onClick={e => e.stopPropagation()}>
              <button onClick={() => setSelected(r)}
                style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #e9e9e4', background: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                View
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16 }}>
          <div style={{ fontSize: 12, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>Page {page} of {totalPages}</div>
          <div style={{ display: 'flex', gap: 6 }}>
            <PagBtn label="← Prev" disabled={page === 1}          onClick={() => setPage(p => p - 1)} />
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const p = Math.max(1, Math.min(page - 2, totalPages - 4)) + i
              return <PagBtn key={p} label={p} active={p === page} onClick={() => setPage(p)} />
            })}
            <PagBtn label="Next →" disabled={page === totalPages} onClick={() => setPage(p => p + 1)} />
          </div>
        </div>
      )}

      <RefundDrawer refund={selected} onClose={() => setSelected(null)} onUpdated={handleUpdated} />
    </div>
  )
}
